import { useEffect, useMemo, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Bot, Sparkles, Users, Scale, Clock, ArrowUpRight } from "lucide-react";
import { PLMJLogo } from "@/components/PLMJLogo";
import { getStats } from "@/lib/api";
import type { FunctionCategory, Stats } from "@/lib/types";
import { cn } from "@/lib/cn";

const PRACTICE_ORDER: FunctionCategory[] = [
  "corporate_ma",
  "litigation",
  "tax",
  "labour",
  "real_estate",
  "banking_finance",
  "ip",
  "competition",
  "tmt",
  "public_law",
  "restructuring",
  "compliance",
  "other",
];

const PRACTICE_LABEL: Record<FunctionCategory, string> = {
  corporate_ma: "Corporate / M&A",
  litigation: "Dispute Resolution",
  tax: "Tax",
  labour: "Employment",
  real_estate: "Real Estate",
  banking_finance: "Banking & Finance",
  ip: "IP",
  competition: "Competition",
  tmt: "TMT",
  public_law: "Public Law",
  restructuring: "Restructuring",
  compliance: "Compliance",
  other: "Other",
};

// Soft gradient chip colors for each practice (used in bars + dots)
const PRACTICE_HUE: Record<FunctionCategory, string> = {
  corporate_ma: "from-cyan-glow to-brand-400",
  litigation: "from-rose-400 to-brand-400",
  tax: "from-emerald-400 to-cyan-glow",
  labour: "from-amber-400 to-brand-400",
  real_estate: "from-lime-400 to-emerald-400",
  banking_finance: "from-emerald-400 to-brand-400",
  ip: "from-fuchsia-400 to-brand-400",
  competition: "from-orange-400 to-brand-400",
  tmt: "from-violet-400 to-brand-500",
  public_law: "from-sky-400 to-cyan-glow",
  restructuring: "from-indigo-400 to-brand-400",
  compliance: "from-teal-400 to-cyan-glow",
  other: "from-white/40 to-white/20",
};

export default function Results() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [loadedAt, setLoadedAt] = useState<Date | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const s = await getStats("all");
        if (!cancelled) {
          setStats(s);
          setLoadedAt(new Date());
        }
      } catch {
        /* keep last state */
      }
    };
    load();
    const t = setInterval(load, 5000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);

  const practiceList = useMemo(() => {
    const b = stats?.byFunction || ({} as Record<FunctionCategory, number>);
    return PRACTICE_ORDER.map((f) => ({
      f,
      count: (b[f] as number) || 0,
    })).sort((a, b) => b.count - a.count);
  }, [stats]);

  const activePractices = practiceList.filter((p) => p.count > 0).length;
  const maxPractice = Math.max(1, ...practiceList.map((f) => f.count));
  const maxName = Math.max(1, ...(stats?.topNames?.slice(0, 10).map((n) => n.count) || [1]));
  const maxWord = Math.max(1, ...(stats?.taskWords?.map((w) => w.count) || [1]));

  return (
    <div className="relative min-h-dvh overflow-hidden px-5 py-8 sm:px-10 sm:py-12">
      <div className="mx-auto max-w-5xl">
        {/* header */}
        <header className="flex items-center justify-between">
          <PLMJLogo />
          <div className="text-[11px] uppercase tracking-[0.25em] text-white/40">
            Workshop results
          </div>
        </header>

        {/* hero — the big number */}
        <section className="mt-16 sm:mt-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="inline-flex items-center gap-2 border border-white/10 bg-white/5 px-3 py-1 text-[11px] uppercase tracking-[0.25em] text-white/60"
          >
            <Sparkles className="h-3 w-3" /> Build your own Harvey
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mx-auto mt-8 max-w-3xl text-[42px] font-bold leading-[1.02] text-white sm:text-[56px]"
          >
            Together we built{" "}
            <CountUp value={stats?.total ?? 0} className="font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-glow to-brand-400" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-glow to-brand-400">
              {" "}AI associates
            </span>
            .
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-white/60"
          >
            Each one was designed in 3 minutes by someone in the room — for a real, recurring task
            in their practice. Together they free up{" "}
            <span className="font-semibold text-white">{stats?.totalHoursSaved ?? 0} hours a week</span>.
          </motion.p>
        </section>

        {/* by the numbers */}
        <Reveal delay={0.2}>
          <section className="mt-20 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <StatCard
              icon={<Users className="h-4 w-4" />}
              label="Associates built"
              value={stats?.total ?? 0}
            />
            <StatCard
              icon={<Clock className="h-4 w-4" />}
              label="Hours saved / week"
              value={stats?.totalHoursSaved ?? 0}
            />
            <StatCard
              icon={<Scale className="h-4 w-4" />}
              label="Practice areas covered"
              value={activePractices}
            />
          </section>
        </Reveal>

        {/* practices */}
        <Reveal delay={0.3}>
          <section className="mt-20">
            <SectionHeader
              index="01"
              icon={<Scale className="h-4 w-4" />}
              title="By practice"
              subtitle="Where across the firm these associates were designed to plug in."
            />
            <div className="glass mt-8 rounded-xl p-6 sm:p-8">
              <div className="space-y-4">
                {practiceList.map(({ f, count }) => (
                  <div key={f} className="grid grid-cols-[120px_minmax(0,1fr)_48px] items-center gap-4 sm:grid-cols-[160px_minmax(0,1fr)_56px]">
                    <div className="flex items-center gap-2">
                      <span
                        className={cn(
                          "h-2.5 w-2.5 rounded-full bg-gradient-to-br",
                          PRACTICE_HUE[f],
                        )}
                      />
                      <span className="text-sm text-white/80">{PRACTICE_LABEL[f]}</span>
                    </div>
                    <div className="h-2 rounded-full bg-white/5 overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${(count / maxPractice) * 100}%` }}
                        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                        className={cn("h-full rounded-full bg-gradient-to-r", PRACTICE_HUE[f])}
                      />
                    </div>
                    <div className="text-right text-sm font-semibold tabular-nums text-white">
                      {count}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </Reveal>

        {/* task word cloud */}
        <Reveal delay={0.35}>
          <section className="mt-20">
            <SectionHeader
              index="02"
              icon={<Sparkles className="h-4 w-4" />}
              title="Tasks being delegated"
              subtitle="The words that came up most in what people wanted their associate to do."
            />
            <div className="glass mt-8 rounded-xl p-8 sm:p-12">
              <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-3 text-center">
                {(stats?.taskWords || []).slice(0, 45).map((w, i) => {
                  const scale = w.count / maxWord;
                  const size = 14 + scale * 38; // 14px .. 52px
                  const opacity = 0.4 + scale * 0.6;
                  return (
                    <motion.span
                      key={w.word}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity, y: 0 }}
                      transition={{
                        duration: 0.5,
                        delay: Math.min(0.02 * i, 0.6),
                        ease: [0.22, 1, 0.36, 1],
                      }}
                      className={cn(
                        "font-medium leading-none tracking-tight",
                        scale > 0.75
                          ? "text-transparent bg-clip-text bg-gradient-to-r from-cyan-glow to-brand-400"
                          : scale > 0.45
                          ? "text-white"
                          : "text-white/70",
                      )}
                      style={{ fontSize: `${size}px` }}
                    >
                      {w.word}
                    </motion.span>
                  );
                })}
                {(!stats || stats.taskWords.length === 0) && (
                  <span className="text-white/40">Waiting for the first task...</span>
                )}
              </div>
            </div>
          </section>
        </Reveal>

        {/* top associate names + roles */}
        <Reveal delay={0.4}>
          <section className="mt-20 grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div className="glass rounded-xl p-6 sm:p-8">
              <SectionHeader
                compact
                index="03"
                icon={<Bot className="h-4 w-4" />}
                title="Most popular associate names"
                subtitle="What the room called the associates they built."
              />
              <div className="mt-6 space-y-3">
                {(stats?.topNames || []).slice(0, 10).map((t, i) => (
                  <div key={t.name + i} className="flex items-center gap-4">
                    <div className="w-6 text-xs tabular-nums text-white/40">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <div className="flex-1 truncate text-sm text-white">{t.name}</div>
                    <div className="h-1.5 w-28 overflow-hidden rounded-full bg-white/5">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${(t.count / maxName) * 100}%` }}
                        transition={{ duration: 0.8 }}
                        className="h-full rounded-full bg-gradient-to-r from-cyan-glow to-brand-400"
                      />
                    </div>
                    <div className="w-8 text-right text-sm tabular-nums text-white">
                      {t.count}
                    </div>
                  </div>
                ))}
                {(!stats || stats.topNames.length === 0) && (
                  <div className="text-white/40 text-sm">No names yet.</div>
                )}
              </div>
            </div>

            <div className="glass rounded-xl p-6 sm:p-8">
              <SectionHeader
                compact
                index="04"
                icon={<Users className="h-4 w-4" />}
                title="Who built them"
                subtitle="Roles that showed up in the room."
              />
              <div className="mt-6 flex flex-wrap gap-2">
                {(stats?.topRoles || []).slice(0, 10).map((r) => (
                  <span
                    key={r.role}
                    className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-xs text-white/80"
                  >
                    <span className="text-white/90">{r.role}</span>
                    <span className="text-[10px] tabular-nums text-white/50">×{r.count}</span>
                  </span>
                ))}
                {(!stats || stats.topRoles.length === 0) && (
                  <span className="text-white/40 text-sm">No roles yet.</span>
                )}
              </div>
            </div>
          </section>
        </Reveal>

        {/* footer */}
        <Reveal delay={0.5}>
          <footer className="mt-24 mb-10 border-t border-white/5 pt-8 text-center">
            <div className="text-xs uppercase tracking-[0.24em] text-white/40">
              Thank you for building with us
            </div>
            <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-white/60">
              Every associate you built is yours to keep. If you want to go deeper — productionise
              one, connect it to your tools, or bring an agentic workflow to your practice — reach out.
            </p>
            <div className="mt-5 flex items-center justify-center gap-3 text-sm">
              <a
                href="mailto:hello@ruicosta.ai?subject=PLMJ%20workshop%20follow-up"
                className="btn-ghost"
              >
                hello@ruicosta.ai
              </a>
              <a
                href="https://www.linkedin.com/in/ruicostapt"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-ghost"
              >
                LinkedIn <ArrowUpRight className="h-3.5 w-3.5" />
              </a>
            </div>
            <div className="mt-8 text-[11px] uppercase tracking-[0.2em] text-white/30">
              PLMJ · Build your own Harvey ·{" "}
              {loadedAt ? loadedAt.getFullYear() : new Date().getFullYear()}
              {loadedAt && <> · updated {loadedAt.toLocaleTimeString()}</>}
            </div>
          </footer>
        </Reveal>
      </div>
    </div>
  );
}

/* ----------------- helpers ----------------- */

function StatCard({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
}) {
  return (
    <div className="glass rounded-xl p-6">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-white/50">
        {icon}
        {label}
      </div>
      <div className="mt-4 text-5xl font-bold tabular-nums text-white">
        <CountUp value={value} />
      </div>
    </div>
  );
}

function SectionHeader({
  index,
  icon,
  title,
  subtitle,
  compact,
}: {
  index: string;
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  compact?: boolean;
}) {
  return (
    <div>
      <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.25em] text-white/50">
        <span className="tabular-nums">§ {index}</span>
        <span className="h-px flex-1 bg-white/10" />
        <span className="flex items-center gap-1.5 text-white/70">
          {icon}
          {title}
        </span>
      </div>
      {subtitle && !compact && (
        <p className="mt-4 max-w-lg text-sm text-white/60">{subtitle}</p>
      )}
      {subtitle && compact && (
        <p className="mt-3 text-sm text-white/60">{subtitle}</p>
      )}
    </div>
  );
}

function Reveal({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const inView = useInView(ref, { once: true, margin: "-10% 0px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 18 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay }}
    >
      {children}
    </motion.div>
  );
}

function CountUp({ value, className }: { value: number; className?: string }) {
  const [display, setDisplay] = useState(value);
  const prevRef = useRef(value);

  useEffect(() => {
    const from = prevRef.current;
    const to = value;
    prevRef.current = to;
    if (from === to) return;
    const start = performance.now();
    const dur = 900;
    let raf = 0;
    const tick = (t: number) => {
      const p = Math.min(1, (t - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(from + (to - from) * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value]);

  return <span className={cn("tabular-nums", className)}>{display.toLocaleString()}</span>;
}
